import { createContext, useCallback, useContext, useState } from "react";
import TwoFactorModal from "@/components/TwoFactorModal";

const Ctx = createContext(null);

export default function TwoFactorProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    label: "",
    handler: null, // async (code) => any
  });

  const close = useCallback(() => {
    setState({ open: false, label: "", handler: null });
  }, []);

  /**
   * requireCode({ actionLabel, run })
   *   - opens the modal, on Confirm calls run(code); modal shows any error
   *     thrown by run() (e.g. backend 412). On successful run() the modal closes.
   *   - returns a Promise that resolves with the run() return value, or rejects on cancel.
   */
  const requireCode = useCallback(({ actionLabel, run }) => {
    return new Promise((resolve, reject) => {
      const handler = async (code) => {
        try {
          const result = await run(code);
          resolve(result);
          return result;
        } catch (e) {
          // Let modal show the error but also reject the outer promise
          reject(e);
          throw e;
        }
      };
      setState({ open: true, label: actionLabel || "", handler });
    });
  }, []);

  const onOpenChange = (open) => {
    if (!open) {
      // If closed without confirming, reject via handler=null path
      close();
    }
  };

  return (
    <Ctx.Provider value={{ requireCode }}>
      {children}
      <TwoFactorModal
        open={state.open}
        actionLabel={state.label}
        onOpenChange={onOpenChange}
        onConfirm={async (code) => {
          if (state.handler) {
            await state.handler(code);
          }
          close();
        }}
      />
    </Ctx.Provider>
  );
}

export function useTwoFactor() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useTwoFactor must be used inside TwoFactorProvider");
  return c;
}
